import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from 'recharts';
import { Badge } from "@/components/ui/badge";
import { Music2, Waves } from "lucide-react";

interface MoodVisualizationProps {
  genres: string[];
  moodScore?: {
    energy: number;
    danceability: number;
    valence: number;
    acousticness: number;
    instrumentalness: number;
  };
}

const genreMoodMap: Record<string, { energy: number; danceability: number; valence: number; acousticness: number; instrumentalness: number }> = {
  'electronic': { energy: 0.8, danceability: 0.9, valence: 0.7, acousticness: 0.2, instrumentalness: 0.6 },
  'techno': { energy: 0.9, danceability: 0.8, valence: 0.6, acousticness: 0.1, instrumentalness: 0.7 },
  'house': { energy: 0.85, danceability: 0.85, valence: 0.75, acousticness: 0.15, instrumentalness: 0.5 },
  'pop': { energy: 0.7, danceability: 0.7, valence: 0.8, acousticness: 0.3, instrumentalness: 0.2 },
  'rock': { energy: 0.8, danceability: 0.6, valence: 0.6, acousticness: 0.4, instrumentalness: 0.3 },
  'jazz': { energy: 0.5, danceability: 0.5, valence: 0.7, acousticness: 0.8, instrumentalness: 0.6 },
  'classical': { energy: 0.3, danceability: 0.2, valence: 0.6, acousticness: 0.9, instrumentalness: 0.9 },
  'hip-hop': { energy: 0.75, danceability: 0.8, valence: 0.65, acousticness: 0.2, instrumentalness: 0.3 },
  'r&b': { energy: 0.6, danceability: 0.7, valence: 0.7, acousticness: 0.4, instrumentalness: 0.2 },
};

function calculateAverageMoodScore(genres: string[]) {
  if (!genres.length) return null;

  const initialScore = {
    energy: 0,
    danceability: 0,
    valence: 0,
    acousticness: 0,
    instrumentalness: 0,
  };

  const score = genres.reduce((acc, genre) => {
    const moodScore = genreMoodMap[genre.toLowerCase()] || {
      energy: 0.5,
      danceability: 0.5,
      valence: 0.5,
      acousticness: 0.5,
      instrumentalness: 0.5,
    };

    return {
      energy: acc.energy + moodScore.energy,
      danceability: acc.danceability + moodScore.danceability,
      valence: acc.valence + moodScore.valence,
      acousticness: acc.acousticness + moodScore.acousticness,
      instrumentalness: acc.instrumentalness + moodScore.instrumentalness,
    };
  }, initialScore);

  // Calculate averages
  Object.keys(score).forEach(key => {
    score[key as keyof typeof score] = score[key as keyof typeof score] / genres.length;
  });

  return score;
}

function getMoodDescription(moodScore: NonNullable<ReturnType<typeof calculateAverageMoodScore>>) {
  const descriptions = [];

  if (moodScore.energy > 0.7) descriptions.push("High-energy");
  else if (moodScore.energy < 0.4) descriptions.push("Relaxed");

  if (moodScore.danceability > 0.7) descriptions.push("Dance-focused");
  if (moodScore.valence > 0.7) descriptions.push("Uplifting");
  else if (moodScore.valence < 0.4) descriptions.push("Introspective");

  if (moodScore.acousticness > 0.7) descriptions.push("Acoustic");
  if (moodScore.instrumentalness > 0.7) descriptions.push("Instrumental");

  return descriptions;
}

export function MoodVisualization({ genres }: MoodVisualizationProps) {
  const moodScore = calculateAverageMoodScore(genres);
  if (!moodScore) return null;

  const chartData = [
    { name: 'Energy', value: moodScore.energy * 100 },
    { name: 'Danceability', value: moodScore.danceability * 100 },
    { name: 'Mood', value: moodScore.valence * 100 },
    { name: 'Acoustic', value: moodScore.acousticness * 100 },
    { name: 'Instrumental', value: moodScore.instrumentalness * 100 },
  ];

  const moodDescriptions = getMoodDescription(moodScore);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Music2 className="h-5 w-5" />
          Event Mood Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
              <PolarGrid />
              <PolarAngleAxis dataKey="name" />
              <Radar
                name="Mood"
                dataKey="value"
                stroke="hsl(var(--primary))"
                fill="hsl(var(--primary))"
                fillOpacity={0.3}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Waves className="h-4 w-4" />
            <span className="text-sm font-medium">Mood Characteristics</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {moodDescriptions.map((desc, i) => (
              <Badge key={i} variant="secondary">
                {desc}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
